import * as React from 'react';
import { Select } from '@components/base/inputs';
import type { SelectOption } from '@components/base/inputs';
import { VStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const sizes = ['sm', 'md', 'lg'] as const;

const policyTypeOptions: SelectOption[] = [
  { value: 'auto', label: 'Auto Insurance' },
  { value: 'home', label: 'Home Insurance' },
  { value: 'life', label: 'Life Insurance' },
  { value: 'health', label: 'Health Insurance' },
  { value: 'business', label: 'Business Insurance', disabled: true },
];

const coverageOptions: SelectOption[] = [
  { value: 'liability', label: 'Liability Coverage' },
  { value: 'collision', label: 'Collision Coverage' },
  { value: 'comprehensive', label: 'Comprehensive Coverage' },
  { value: 'uninsured', label: 'Uninsured Motorist' },
  { value: 'medical', label: 'Medical Payments' },
];

function SelectShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  const [selectedValue, setSelectedValue] = React.useState<string>('');
  const [multiSelectedValues, setMultiSelectedValues] = React.useState<string[]>([]);

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="select">
          <div className="max-w-md">
            <Select
              label="Policy Type"
              placeholder="Select a policy type"
              options={policyTypeOptions}
              size={playgroundProps.size as 'sm' | 'md' | 'lg'}
              disabled={playgroundProps.disabled as boolean}
              required={playgroundProps.required as boolean}
              error={playgroundProps.error as boolean}
              errorMessage={playgroundProps.error ? 'Please select a policy type' : undefined}
              value={selectedValue}
              onChange={(value) => { setSelectedValue(value as string); }}
            />
          </div>
      </ShowcasePreviewSection>

      {/* Size Variants */}
      <ShowcaseDemoSection
        title="Size Variants"
        description="Select fields come in three sizes: small (32px), medium (40px), and large (48px)"
       
      >
          <VStack gap={4} className="max-w-md">
            {sizes.map((size) => (
              <Select
                key={size}
                label={`${size.toUpperCase()} Size`}
                placeholder={`${size} select`}
                options={policyTypeOptions}
                size={size}
              />
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* With Labels */}
      <ShowcaseDemoSection
        title="With Labels"
        description="Select fields with descriptive labels"
       
      >
          <VStack gap={4} className="max-w-md">
            <Select
              label="Insurance Type"
              placeholder="Choose insurance type"
              options={policyTypeOptions}
              helperText="Select the type of insurance you need"
            />
            <Select
              label="Coverage Level"
              placeholder="Choose coverage"
              options={coverageOptions}
              helperText="Additional coverages can be added later"
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Required Fields */}
      <ShowcaseDemoSection
        title="Required Fields"
        description="Required select fields display an asterisk indicator"
       
      >
          <VStack gap={4} className="max-w-md">
            <Select
              label="Primary Coverage"
              placeholder="Select coverage"
              options={coverageOptions}
              required
            />
            <Select
              label="Policy Term"
              placeholder="Select term"
              options={[
                { value: '6mo', label: '6 Months' },
                { value: '12mo', label: '12 Months' },
                { value: '24mo', label: '24 Months' },
              ]}
              required
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Error State */}
      <ShowcaseDemoSection
        title="Error State"
        description="Select fields with validation errors"
       
      >
          <VStack gap={4} className="max-w-md">
            <Select
              label="Coverage Type"
              placeholder="Select coverage"
              options={coverageOptions}
              error
              errorMessage="Coverage selection is required"
            />
            <Select
              label="Deductible Amount"
              placeholder="Select deductible"
              options={[
                { value: '250', label: '$250' },
                { value: '500', label: '$500' },
                { value: '1000', label: '$1,000' },
              ]}
              value="250"
              error
              errorMessage="Selected deductible is not available for this policy"
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Disabled State */}
      <ShowcaseDemoSection
        title="Disabled State"
        description="Disabled select fields cannot be interacted with"
       
      >
          <VStack gap={4} className="max-w-md">
            <Select
              label="Policy Status"
              options={[
                { value: 'active', label: 'Active' },
                { value: 'pending', label: 'Pending' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
              value="active"
              disabled
              helperText="Policy status cannot be changed manually"
            />
            <Select
              label="Coverage Region"
              placeholder="Select region"
              options={[
                { value: 'us', label: 'United States' },
                { value: 'ca', label: 'Canada' },
              ]}
              disabled
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* With Disabled Options */}
      <ShowcaseDemoSection
        title="Disabled Options"
        description="Individual options can be disabled within the dropdown"
       
      >
          <div className="max-w-md">
            <Select
              label="Insurance Type"
              placeholder="Select insurance"
              options={policyTypeOptions}
              helperText="Business Insurance is currently unavailable"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Multi-select */}
      <ShowcaseDemoSection
        title="Multi-select"
        description="Allow selection of multiple options"
       
      >
          <VStack gap={4} className="max-w-md">
            <Select
              label="Additional Coverages"
              placeholder="Select coverages"
              options={coverageOptions}
              multiple
              value={multiSelectedValues}
              onChange={(value) => { setMultiSelectedValues(value as string[]); }}
              helperText={`${multiSelectedValues.length} coverage(s) selected`}
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Searchable */}
      <ShowcaseDemoSection
        title="Searchable Select"
        description="Enable search/filter functionality within the dropdown"
       
      >
          <div className="max-w-md">
            <Select
              label="Find Policy Type"
              placeholder="Search policies..."
              options={[
                ...policyTypeOptions,
                { value: 'umbrella', label: 'Umbrella Insurance' },
                { value: 'renters', label: 'Renters Insurance' },
                { value: 'pet', label: 'Pet Insurance' },
                { value: 'travel', label: 'Travel Insurance' },
                { value: 'disability', label: 'Disability Insurance' },
              ]}
              searchable
              helperText="Type to filter options"
            />
          </div>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const SelectShowcase: ShowcaseDefinition = {
  id: 'select',
  name: 'Select',
  category: 'inputs',
  description: 'Dropdown selection from a list of options',
  propsDefinition: [
    {
      name: 'size',
      type: 'select',
      options: ['sm', 'md', 'lg'],
      defaultValue: 'md',
      description: 'Size of the select (sm=32px, md=40px, lg=48px)',
    },
    {
      name: 'disabled',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the select is disabled',
    },
    {
      name: 'required',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the field is required (shows asterisk)',
    },
    {
      name: 'error',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the field is in an error state',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="combobox" on trigger button',
      'aria-expanded (dropdown state)',
      'aria-controls (listbox reference)',
      'aria-selected on options',
      'aria-disabled on disabled options',
    ],
    keyboardNav: [
      'Enter/Space: Open dropdown or select option',
      'ArrowDown/ArrowUp: Navigate options',
      'Home/End: Jump to first/last option',
      'Escape: Close dropdown',
      'Type character: Jump to matching option',
    ],
    notes: [
      'Uses listbox pattern for dropdown',
      'Focus management between trigger and options',
      'Supports type-to-search navigation',
    ],
  },
  component: SelectShowcaseComponent,
};
